<template>
  <div class="d-flex flex-column mr-3">
    <VueLightbox
      v-if="lightboxVisible"      
      :show-gallery="true"
      :index="lightboxIndex"
      :items="getSelectedCreativeReviewUrls"      
      :toolbar="['counter', 'zoom','fullscreen','gallery','rotate','close']"
      :click-outside-hide="true"
      :z-index="1099"
      @close="lightboxVisible = false"
    >
      <template        
        v-slot:loading
      >
        <div>
          <h5 class="pt-5 text-center">
            Loading files...
          </h5>   
        </div>
      </template>          
    </VueLightbox>

    <b-toast
      v-if="selectedCreativeReview"
      id="review-creative-popup"      
      v-model="lightboxVisible"
      :title="getReviewPopupTitle" 
      no-auto-hide
      no-close-button
      toaster="b-toaster-bottom-right"
      solid      
    >
      <div>       
        <b-button
          v-if="selectedCreativeReview.status !== 'Approved'"
          variant="success"
          size="md"
          class="mr-3"
          @click="showConfirmApproveCreative"
        >         
          Approve
        </b-button>
        <b-button
          v-if="selectedCreativeReview.status !== 'Rejected'"
          variant="danger"
          size="md"
          @click="showConfirmRejectCreative"
        >
          Reject
        </b-button>         
      </div>
    </b-toast>
    
    <b-modal
      id="review-modal"
      ref="review-modal"
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      title="Review Creative"
      ok-title="Approve"
      cancel-title="Reject"
      button-size="sm"
      @ok="approveCreative"
      @cancel="rejectCreative"
    >
      <p
        class="mb-2"
      >
        There are no media files uploaded. Do you want to <strong>approve</strong> or <strong>reject</strong> the creative?
      </p>

      <template #modal-footer>
        <BaseButtonLoader
          :loading="isLoadingApproved"
          :disabled="isLoadingRejected"
          type="button"
          :custom-button="false"
          size="sm"
          variant="primary"
          @click="approveCreative"
        >
          Approve
        </BaseButtonLoader>

        <BaseButtonLoader
          type="button"
          variant="danger"
          :loading="isLoadingRejected"
          :custom-button="false"
          size="sm"
          class="ml-2"
          :disabled="isLoadingApproved"
          @click="rejectCreative"
        >
          Reject
        </BaseButtonLoader>
      </template>
    </b-modal>

    <div class="d-flex">
      <b-button
        v-b-tooltip.hover
        variant="primary"
        size="sm"
        class="mb-3" 
        title="Check creatives you want to approve in bulk"
        :disabled="selectedCreatives.length === 0"
        @click="showBulkApproval"
      >
        <font-awesome-icon
          class="mr-1"       
          :icon="['fas', 'check']"
        />
        Approve Selected Creatives
      </b-button>      
    </div>   
   
    <b-table
      ref="selectableTable"
      :fields="fields"          
      :items="filteredReviews"
      responsive
      :per-page="pageSize"
      :current-page="currentPage"
      thead-class="text-nowrap"
      tbody-tr-class="text-nowrap"
      primary-key="id"
      show-empty      
      :filter="filterByCreativeId"
      :filter-function="filterByCreativeIdField"     
    >
      <template #empty>
        <h6><i /></h6>
      </template>

      <template
        v-if="!simpleView"
        v-slot:top-row
      >
        <td
          v-for="(field, key) in fields" 
          :key="key"
          :class="field.tdClass"
        >
          <div v-if="Object.keys(filters).includes(field.key)">
            <input
              v-if="!dropdownFilterOptions[field.key]"
              v-model="filters[field.key]"
              :placeholder="field.label"
              class="form-control custom-form-input--xs"
              :class="field.tdClass"
            >
            <b-form-select
              v-else
              v-model="filters[field.key]"
              :options="dropdownFilterOptions[field.key].filterOptions"
              class="form-control custom-form-input--xs"
              :placeholder="dropdownFilterOptions[field.key].placeholder"
              :class="dropdownFilterOptions[field.key].customClass"
            />
          </div>
        </td>
      </template>      

      <template #cell(status)="row">
        <b-badge
          v-if="row.item.status"
          tag="div"
          class="fixed-width-100 py-1"
          :variant="getStatusMap(row.item.status).variant"
        >
          {{ getStatusMap(row.item.status).text }}
        </b-badge>
      </template>

      <template #cell(creativeId)="row">
        <div class="d-flex align-items-center">
          <div class="text-truncate max-width-150">
            {{ row.item.creativeId }}
          </div>
        
          <a
            v-clipboard
            :data-copy-text="row.item.creativeId"
            data-copy-success="Copied creative ID"
            class="ml-2 clickable"
          >
            <font-awesome-icon
              :icon="['fa', 'copy']"
              size="sm"
            />
          </a>
        </div>
      </template>

      <template #cell(cmsId)="row">
        {{ row.item.cmsId }}
      </template>
  
      <template #cell(landingPageUrl)="row">
        <TruncatedLink
          v-if="row.item.landingPageUrl"
          :text="row.item.landingPageUrl"
          :href="row.item.landingPageUrl"
          :title="row.item.landingPageUrl"
        />
      </template>
  
      <template #cell(categoryName)="row">
        {{ row.item.categoryName }}
      </template>
  
      <template #cell(trafficSourceName)="row">
        {{ row.item.trafficSourceName }}
      </template>
  
      <template #cell(country)="row">
        {{ row.item.country }}
      </template>
  
      <template #cell(language)="row">
        {{ row.item.language }}
      </template>

      <template #cell(marketCategory)="row">        
        <div v-if="row.item.isMedicare">
          {{ row.item.marketCategory }}
        </div>        
      </template>

      <template #head(checkbox)>
        <b-form-checkbox
          :indeterminate="selectedCreatives.length > 0 && selectedCreatives.length < filteredReviews.length"
          :checked="selectedCreatives.length === filteredReviews.length"
          @change="toggleSelectAll"
        />       
      </template>

      <template #cell(checkbox)="row">
        <b-form-checkbox
          v-if="row.item.status === 'Pending'"
          v-model="row.item._selected"
          @change="onRowToggled(row.item)"
        />
      </template>
  
      <template #cell(actions)="row">
        <div class="d-flex justify-content-center">
          <ButtonLoader
            v-if="row.item && row.item.status === 'Pending'"
            size="sm"
            variant="warning"            
            title="Review"
            :custom-button="false"
            :loading="Boolean(loadingFiles[row.item.id])"
            show-text-with-loader
            @click="loadCreativeReview(row.item)"
          >
            Review
          </ButtonLoader>

          <ButtonLoader
            v-if="row.item && row.item.status !== 'Pending' && row.item.creative.files && row.item.creative.files.length > 0"
            size="sm"        
            variant="primary"
            title="Show Media Files"
            :custom-button="false"
            :loading="Boolean(loadingFiles[row.item.id])"
            show-text-with-loader
            @click="loadCreativeReview(row.item)"
          >
            View
          </ButtonLoader>
        </div>
      </template>
    </b-table>

    <div class="d-flex justify-content-center">
      <b-pagination
        v-model="currentPage"
        size="sm"
        :total-rows="filteredReviews.length"
        :per-page="pageSize"
      />
    </div>

    <b-modal
      id="confirm-approve-modal"
      ref="confirm-approve-modal"
      header-class="custom-modal"
      body-class="custom-modal"

      footer-class="custom-modal"
      title="Confirm Creative Approval"
      ok-title="Approve"
      button-size="sm"
      @ok="approveCreative"
    >
      <p
        v-if="selectedCreativeReview && selectedCreativeReview.creative"
        class="mb-2"
      >
        Are you sure you want to approve <strong>{{ selectedCreativeReview.creative.id }}</strong>?
      </p>

      <template #modal-footer>
        <b-button
          type="button"
          variant="secondary"
          size="sm"
          :disabled="isLoadingApproved"
          @click="$bvModal.hide('confirm-approve-modal')"
        >
          Cancel
        </b-button>

        <BaseButtonLoader
          :loading="isLoadingApproved"
          class="ml-2"
          type="button"
          :custom-button="false"
          size="sm"
          variant="primary"
          @click="approveCreative"
        >
          Approve
        </BaseButtonLoader>
      </template>
    </b-modal>

    <!-- Creative Rejection Modal -->
    <validation-observer
      ref="formValidation"
      v-slot="{ handleSubmit, invalid }"
    >
      <b-form
        novalidate
        @submit.stop.prevent="handleSubmit(rejectCreative)"
      >
        <b-modal
          id="confirm-reject-modal"
          ref="confirm-reject-modal"
          header-class="custom-modal"
          body-class="custom-modal"
          footer-class="custom-modal"
          title="Confirm Creative Rejection"
          ok-title="Reject"
          button-size="sm"
          size="lg"
          @ok="rejectCreative"
        >
          <p
            v-if="selectedCreativeReview && selectedCreativeReview.creative"
            class="mb-3"
          >
            Are you sure you want to reject <strong>{{ selectedCreativeReview.creative.id }}</strong>?
          </p>

          <BaseFormTextArea
            v-model="creativeRejectionReason"
            :horizontal="true"
            :small="true"
            internal-platform-input
            name="Rejection reason"
            label="Rejection reason"
            width-lg="5"
            width-xl="3"       
            rows="3"
            plaintext
            required
            input-class="px-0"
            placeholder="Please provide rejection reason"
          />

          <template #modal-footer>
            <b-button
              type="button"
              variant="secondary"
              size="sm"
              :disabled="isLoadingRejected"
              @click="$bvModal.hide('confirm-reject-modal')"
            >
              Cancel
            </b-button>

            <BaseButtonLoader
              :loading="isLoadingRejected"
              class="ml-2"
              type="submit"
              :custom-button="false"
              size="sm"
              variant="danger"
              :disabled="invalid"
              @click="rejectCreative"
            >
              Reject
            </BaseButtonLoader>
          </template>
        </b-modal>

        <b-modal
          :id="bulkModalApprovalModalId"
          :ref="bulkModalApprovalModalId"
          header-class="custom-modal"
          body-class="custom-modal" 
          footer-class="custom-modal"
          :title="`Are you sure you want to approve following ${selectedCreatives.length} creatives?`"
          button-size="sm"
          @ok="bulkApproveCreativesModal"   
        >
          <template #modal-footer>
            <BaseButtonLoader
              :loading="isLoadingBulkApproved"
              type="button"
              :custom-button="false"
              size="sm"
              variant="primary"
              @click="bulkApproveCreativesModal"
            >
              Confirm
            </BaseButtonLoader>

            <BaseButtonLoader
              type="button"
              variant="danger"
              :disabled="isLoadingBulkApproved"
              :custom-button="false"
              size="sm"
              class="ml-2"          
              @click="$bvModal.hide(bulkModalApprovalModalId)"
            >
              Cancel
            </BaseButtonLoader>
          </template>
        </b-modal>        
      </b-form>
    </validation-observer>
  </div>
</template>
        
<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import VueLightbox from 'vue-lightbox-lite'
import 'vue-lightbox-lite/dist/index.css'

import { showErrorMessage } from '@/notification-utils'
import formInputMixin from '@/mixins/formInputMixin'
import copyToClipboardMixin from '@/mixins/copy-to-clipboard-mixin'
import TruncatedLink from '@/components/common/TruncatedLink.vue'
        
export default {
  name: 'ListCreativeReviews',
  
  components: {
    VueLightbox,
    TruncatedLink
  },

  mixins: [formInputMixin, copyToClipboardMixin],

  props: {
    simpleView: {
      type: Boolean,
      default: false
    },

    creativeReviews: {
      type: Array,
      default() {
        return []
      }
    },

    statusFilter: {
      type: String,
      default: 'Pending'
    },

    filterByCreativeId: {
      type: String,
      default: ''
    },

    listNameSuffix: {
      type: String,
      default: ''
    }
  },
        
  data() {
    return {
      lightboxVisible: false,
      lightboxIndex: 0,
      currentLightboxIndex: 0,
      loadingFiles: {},
      isLoadingRejected: false,
      isLoadingApproved: false,
      isLoadingBulkApproved: false,      
      shouldReview: false,
      creativeRejectionReason: '',
      pageSize: 15,
      currentPage: 1,
      filters: {
        creativeId: '',
        status: this.statusFilter,       
        cmsId: '',
        landingPageUrl:'',
        categoryName:'',
        trafficSourceName: '',
        country:'',
        language:'',
        marketCategory: ''       
      },
      selectedCreatives: [],      
      bulkModalApprovalModalId: 'bulk-approve-modal-' + this.listNameSuffix,
      dropdownFilterOptions: {
        'status': {
          filterOptions: Object.freeze([{
            value: null,
            text: 'Filter by Status'
          },
          {
            value: 'Pending',
            text: 'Review Pending'
          },
          {
            value: 'Rejected',
            text: 'Rejected'
          },
          {
            value: 'Approved',
            text: 'Approved'
          }]),
          customClass: 'fixed-width-140',
          placeholder: 'Status'
        },
        'marketCategory': {
          filterOptions: Object.freeze([{
            value: null,
            text: 'Filter by Type'
          },
          {
            value: 'Unknown',
            text: 'Unknown'
          },
          {
            value: 'Communication',
            text: 'Communication'
          },
          {
            value: 'Marketing',
            text: 'Marketing'
          }]),
          customClass: 'fixed-width-140',
          placeholder: 'Type'
        }
      }
    }
  },
  
  computed: {
    ...mapState('creative', [ 'selectedCreativeReview' ]),
    
    ...mapGetters('creative', ['getSelectedCreativeReviewUrls']),    

    ...mapGetters('auth', [
      'accountDetails'
    ]),

    fields() {
      const columns = [ 
        { key: 'checkbox', label: '', sortable: false },
        { key: 'actions', label: 'Review Creative', sortable: false, tdClass: 'max-width-150', thClass: 'text-center' }
      ]

      if (!this.simpleView) {
        columns.push({ key: 'status', label: 'Status', sortable: !this.simpleView, thClass: 'text-center', tdClass: 'text-center' })
      }

      columns.push(
        { key: 'creativeId', label: 'ID', sortable: !this.simpleView },       
        { key: 'cmsId', label: 'SMID (CMS ID)', sortable: !this.simpleView },  
        { key: 'marketCategory', label: 'Type', sortable: !this.simpleView },    
        { key: 'landingPageUrl', label: 'Landing Page URL', sortable: !this.simpleView, tdClass: 'max-width-150' },
        { key: 'categoryName', label: 'Category', sortable: !this.simpleView },
        { key: 'trafficSourceName', label: 'Traffic Source', sortable: !this.simpleView },       
        { key: 'country', label: 'Country', sortable: !this.simpleView },
        { key: 'language', label: 'Language', sortable: !this.simpleView }
      )

      return columns
    },

    filteredReviews () { 
      const formatted = this.creativeReviews.map(review => { 
        return {
          id: review.id,        
          status: review.status,         
          cmsId: review.creative.cmsId,
          landingPageUrl: review.creative.landingPageUrl,
          categoryName: review.creative.categoryName,
          trafficSourceName: review.creative.trafficSourceName,
          country: review.creative.countryCode,
          language: review.creative.languageCode,
          creative: review.creative,
          files: review.creative.files,
          creativeId: review.creative.id,
          marketCategory: review.creative.marketCategory,
          isMedicare: review.creative.isMedicare
        }
      })      

      const filtered = formatted.filter(item => {
        return Object.keys(this.filters).every(key => {
          if (this.filters[key] != null) {
            return item[key]?.trim()?.toLowerCase().includes(this.filters[key]?.toLowerCase())
          }

          return true
        })
      })
      
      return filtered
    },

    filteredPendingReviews() {
      return this.filteredReviews.filter(cr => cr.status === 'Pending')
    },

    getReviewPopupTitle() {
      if (this.selectedCreativeReview){
        switch (this.selectedCreativeReview.status) {
        case 'Pending':
          return 'Review creative'
        case 'Approved':
          return 'Creative has been already approved'
        case 'Rejected':     
          return 'Creative has been already rejected'  
        default:
          return ''
        }
      }
      return ''
    }
  },
  
  methods: {
    ...mapActions('creative', [ 
      'getCreativeReview', 
      'approveCreativeReview', 
      'rejectCreativeReview',
      'bulkApproveCreatives'
    ]),     
    ...mapMutations('creative', ['SET_SELECTED_CREATIVE_REVIEW']),

    filterByCreativeIdField(item, filter = '') {
      return item.creative?.id?.toLowerCase().includes(filter.toLowerCase()) 
             || item.creative?.cmsId?.toLowerCase().includes(filter.toLowerCase())
    },
  
    getStatusMap(status) {
      const statusToVariantMap = {
        Pending: {variant:'secondary', text:'Review Pending'},
        Rejected: {variant:'danger', text:'Rejected'},
        Approved: {variant:'success', text:'Approved'},
        Disabled: {variant:'warning', text:'Disabled'}        
      }

      return statusToVariantMap[status] ?? {variant:'dark', text: ''}
    },

    showConfirmApproveCreative() {      
      this.$bvModal.show('confirm-approve-modal')
      this.lightboxVisible = false
    },

    showConfirmRejectCreative() {      
      this.$bvModal.show('confirm-reject-modal')
      this.lightboxVisible = false
    },
  
    async loadCreativeReview(review){
      if (!review.creative.files || !review.creative.files.length) {
        this.SET_SELECTED_CREATIVE_REVIEW(review)
        this.$bvModal.show('review-modal')
        return
      }

      try {
        this.currentLightboxIndex = 0

        this.$set(this.loadingFiles, review.id, true)

        await this.getCreativeReview(review.id)

        this.lightboxVisible = true

        this.shouldReview = true
      } catch (error) {
        showErrorMessage("Failed to open files")
      } finally {
        this.$set(this.loadingFiles, review.id, false)
      }
    },

    async approveCreative(){
      try {
        this.isLoadingApproved = true

        await this.approveCreativeReview(this.selectedCreativeReview.id)

        this.$bvModal.hide('confirm-approve-modal')
        this.$bvModal.hide('review-modal')
      } catch (error) {
        showErrorMessage("Failed to approve creative")
      } finally {
        this.isLoadingApproved = false
      }
    },

    async rejectCreative(){
      try {
        this.isLoadingRejected = true
        
        await this.rejectCreativeReview({
          id: this.selectedCreativeReview.id, 
          reason: this.creativeRejectionReason
        })
        
        this.$bvModal.hide('confirm-reject-modal')
        this.$bvModal.hide('review-modal')

        this.creativeRejectionReason = ''
      } catch (error) {
        showErrorMessage("Failed to reject creative")
      } finally {
        this.isLoadingRejected = false
      }
    },

    onImageIndexChange(oldIndex, newIndex) {
      this.currentLightboxIndex = newIndex
    },

    showBulkApproval() {
      this.$bvModal.show(this.bulkModalApprovalModalId)
    },
    
    toggleSelectAll(checked) {
      this.filteredPendingReviews.forEach(item => {
        item._selected = checked
      })
      this.selectedCreatives = checked ? [...this.filteredPendingReviews] : []
    },

    onRowToggled(item) {
      if (item._selected) {
        if (!this.selectedCreatives.find(i => i.id === item.id) && item.status === 'Pending') {
          this.selectedCreatives.push(item)
        }
      } else {
        this.selectedCreatives = this.selectedCreatives.filter(i => i.id !== item.id)
      }
    },

    async bulkApproveCreativesModal() {
      try {
        this.isLoadingBulkApproved = true
        
        await this.bulkApproveCreatives({
          ids: this.selectedCreatives.map(c => c.id) 
        })
        
        this.$bvModal.hide(this.bulkModalApprovalModalId)
        this.toggleSelectAll(false)
        this.selectedCreatives = []
      } catch (error) {
        showErrorMessage("Failed to approve multiple creatives")
      } finally {
        this.isLoadingBulkApproved = false
      }
    }    
  }
}
</script>
  <style lang="scss" scoped>
  </style>